import { Box, HStack, Switch, Text, VStack } from "@chakra-ui/react";
import { repeat, uniq } from "ramda";
import { Chart } from "react-chartjs-2";
import { DashboardData, findSampleMeasurementByDate, SampleRecord, getSampleDate } from "../../types/dashboardData";
import { useEffect, useMemo, useState } from "react";
import { ColorContext, useSitesColors } from "../SiteColorsProvider/SiteColorsProvider";

export function GenericDoubleBarGraph({
  dashboardData,
  sites,
  analyte,
  showReferenceLine,
  setShowReferenceLine,
  colorContext,
}: {
  dashboardData: DashboardData;
  sites: string[];
  analyte: string;
  showReferenceLine: boolean;
  setShowReferenceLine: (showReferenceLine: boolean) => void;
  colorContext: ColorContext;
}) {
  const samples = dashboardData.samples;
  const uniqueDates = useUniqueDates(samples);
  const { referenceLinesY, referenceLineYPlugin } = useReferenceLines(showReferenceLine);

  const data = useGraphDataset(sites, dashboardData, showReferenceLine, uniqueDates, colorContext);
  const dates: string[] = uniq(samples.map(getSampleDate)).sort();
  const hasReferenceLimits = Object.values(dashboardData.referenceLimits).some((x) => x !== null);

  return (
    <VStack alignItems={"stretch"} height="100%" flex={1} minHeight={"500px"}>
      <HStack flex={1} overflowX={"auto"} height={"100%"} alignItems={"stretch"}>
        <Box minWidth="400px" flex={1}>
          <Chart
            type="bar"
            options={options(dashboardData.unit || "", dates[0], dates[dates.length - 1], analyte)}
            data={data}
            plugins={[referenceLineYPlugin]}
            height="100%"
          />
        </Box>

        {showReferenceLine && hasReferenceLimits && (
          <Box position="relative" width="200px">
            {referenceLinesY.map((referenceLine) => (
              <Text key={referenceLine.id} position={"absolute"} top={`${referenceLine.y}px`} color="red" width="200px">
                {referenceLine.id}
              </Text>
            ))}
          </Box>
        )}
      </HStack>
      {hasReferenceLimits && (
        <Box textAlign={"center"}>
          <Switch
            isChecked={showReferenceLine}
            onChange={(e) => setShowReferenceLine(e.target.checked)}
            colorScheme="red"
          >
            Show Reference Line
          </Switch>
        </Box>
      )}
    </VStack>
  );
}

function useUniqueDates(samples: SampleRecord[]) {
  return useMemo(() => uniq(samples.map(getSampleDate)).sort(), [samples]);
}

interface ReferenceLineY {
  id: string;
  y: number;
}

function useReferenceLines(showReferenceLine: boolean) {
  const [referenceLinesY, setReferenceLinesY] = useState<ReferenceLineY[]>([]);

  useEffect(() => {
    setReferenceLinesY([]);
  }, [showReferenceLine]);

  const referenceLineYPlugin = useMemo(
    () => ({
      id: "useReferenceLineYPlugin",
      afterDraw: function (chart: any) {
        setReferenceLinesY((referenceLinesY) => {
          return getUpdatedReferenceLines(chart, referenceLinesY);
        });
      },
    }),
    [],
  );
  return { referenceLinesY, referenceLineYPlugin };
}

function getUpdatedReferenceLines(chart: any, referenceLinesY: ReferenceLineY[]) {
  const newValues = getReferenceLinesYUpdatedValues(chart, referenceLinesY);
  if (newValues.length > 0) {
    return [
      ...newValues,
      ...referenceLinesY.filter((referenceLine) => !newValues.some((x) => x.id === referenceLine.id)),
    ];
  }
  return referenceLinesY;
}

function getReferenceLinesYUpdatedValues(chart: any, referenceLinesY: ReferenceLineY[]) {
  const newValues: ReferenceLineY[] = [];

  chart._metasets.forEach((dataset: any) => {
    if (dataset.type !== "line" || dataset.data.length === 0) return;

    const y = Math.round(dataset.data[0].y - 15);
    const referenceLine = referenceLinesY.find(
      (referenceLine) => referenceLine.id === dataset._dataset.referenceLineId,
    );
    if (referenceLine === undefined) {
      newValues.push({ id: dataset._dataset.referenceLineId, y });
      return;
    } else if (referenceLine.y !== y) {
      newValues.push({ ...referenceLine, y });
    }
  });

  return newValues;
}

interface ReferenceLineInGraph {
  id: string;
  limit: number;
}

function useGraphDataset(
  sites: string[],
  dashboardData: DashboardData,
  showReferenceLine: boolean,
  uniqueDates: string[],
  colorContext: ColorContext,
) {
  const samples = dashboardData.samples;
  const barDataset = useBarDataset(sites, samples, uniqueDates, colorContext);
  const referenceLines = useMemo<ReferenceLineInGraph[]>(
    () => [
      ...(dashboardData.referenceLimits.aquaticLife
        ? [{ id: "Aquatic Life Reference", limit: dashboardData.referenceLimits.aquaticLife }]
        : []),
      ...(dashboardData.referenceLimits.humanHealth
        ? [{ id: "Human Health Reference", limit: dashboardData.referenceLimits.humanHealth }]
        : []),
      ...(dashboardData.referenceLimits.drinkingWater
        ? [{ id: "Drinking Water Reference", limit: dashboardData.referenceLimits.drinkingWater }]
        : []),
    ],
    [dashboardData.referenceLimits],
  );
  const referenceLineDatasets = useMemo(
    () =>
      referenceLines.map((referenceLine) => ({
        type: "line" as const,
        data: repeat(referenceLine.limit, uniqueDates.length + 3),
        borderColor: "red",
        backgroundColor: "red",
        label: "Reference Limit " + referenceLine.id,
        borderDash: [10, 5],
        referenceLineId: referenceLine.id,
      })),
    [referenceLines, uniqueDates.length],
  );

  return useMemo(
    () => createGraphData(barDataset, uniqueDates, showReferenceLine ? referenceLineDatasets : []),
    [barDataset, uniqueDates, showReferenceLine, referenceLineDatasets],
  );
}

function useBarDataset(sites: string[], samples: SampleRecord[], uniqueDates: string[], colorContext: ColorContext) {
  const sitesColor = useSitesColors(sites, colorContext);

  return useMemo(
    () =>
      sites.map((site) => ({
        type: "bar" as const,
        label: site,
        data: [null, ...uniqueDates.map((date) => findSampleMeasurementByDate(site, date, samples)), null],
        backgroundColor: sitesColor[site],
        minBarLength: 7,
      })),
    [sites, samples, uniqueDates, sitesColor],
  );
}

function createGraphData(
  barDataset: ReturnType<typeof useBarDataset>,
  uniqueDates: string[],
  referenceLineDatasets: any[],
) {
  return {
    labels: ["1000-00-00", ...uniqueDates, "9999-00-00"],
    datasets: [...referenceLineDatasets, ...barDataset],
  };
}

export const options = (unit: string, minX: string, maxX: string, analyte: string) => ({
  maintainAspectRatio: false,
  responsive: true,
  scales: {
    y: {
      ticks: {
        callback: function (value: string | number, index: number, ticks: any) {
          return `${value} ${unit}`;
        },
      },
      title: { display: true, text: analyte, font: { size: 20 } },
    },
    x: { min: minX, max: maxX },
  },
  elements: {
    point: {
      radius: 0,
    },
  },
  plugins: {
    legend: {
      labels: {
        filter: function (legendItem: { text: string }) {
          return !legendItem.text.startsWith("Reference Limit");
        },
      },
      title: {
        text: "Sampling Site Names",
        display: true,
        font: { size: 16 },
      },
    },
  },
  animation: {
    active: {
      duration: 0,
    },
    onComplete: function (this: any) {
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "bottom";

      this.data.datasets.forEach((dataset: any, i: any) => {
        var meta = this.getDatasetMeta(i);

        meta.data.forEach((bar: any, index: any) => {
          var data = dataset.data[index];
          if (data !== 0 || bar.x === 0) return;
          this.ctx.fillText(data, bar.x, bar.y - 5);
        });
      });
    },
  },
});
